<template>
  <div class="ak-pagination">
    <div class="ak-pagination__section">
      <ak-pagination-button
        :page="
          getPage(1, {
            class: 'ak-pagination__item',
            disabled: current === 1,
          })
        "
      >
        <ak-icon
          class="ak-pagination__chevron"
          type="chevron-left"
          size="small"
        />
        <ak-icon
          class="ak-pagination__chevron"
          type="chevron-left"
          size="small"
        />
      </ak-pagination-button>
    </div>

    <div class="ak-pagination__section">
      <ak-pagination-button
        :page="
          getPage(current - 1, {
            class: 'ak-pagination__item',
            disabled: !hasPrev,
          })
        "
      >
        <ak-icon
          class="ak-pagination__chevron"
          type="chevron-left"
          size="small"
        />
      </ak-pagination-button>
    </div>

    <div class="ak-pagination__section">
      <ul class="ak-pagination__numbers">
        <li
          v-for="page in pagesInRange"
          :key="page.number"
          class="ak-pagination__numbers-item"
        >
          <ak-pagination-button :page="page" />
        </li>
      </ul>
    </div>

    <div class="ak-pagination__section">
      <ak-pagination-button
        :page="
          getPage(current + 1, {
            class: 'ak-pagination__item',
            disabled: !hasNext,
          })
        "
      >
        <ak-icon
          class="ak-pagination__chevron"
          type="chevron-right"
          size="small"
        />
      </ak-pagination-button>
    </div>

    <div class="ak-pagination__section">
      <ak-pagination-button
        :page="
          getPage(pageCount, {
            class: 'ak-pagination__item',
            disabled: current === pageCount,
          })
        "
      >
        <ak-icon
          class="ak-pagination__chevron"
          type="chevron-right"
          size="small"
        />
        <ak-icon
          class="ak-pagination__chevron"
          type="chevron-right"
          size="small"
        />
      </ak-pagination-button>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, nextTick, watch } from 'vue'
import PaginationButton from './PaginationButton.vue'
import Icon from '../icon/Icon.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-pagination total="100" current="1" />
   */
  name: 'AkPagination',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [PaginationButton.name]: PaginationButton,
    [Icon.name]: Icon,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Number|String}
     * @description The total number of items that we are paginating.
     */
    total: {
      default: 0,
      type: [Number, String],
    },

    /**
     * @type {Number|String}
     * @description The number of items to display per page.
     */
    perPage: {
      default: 20,
      type: [Number, String],
    },

    /**
     * @type {Number|String}
     * @description The current page number.
     */
    current: {
      default: 1,
      type: [Number, String],
    },

    /**
     * @type {Number|String}
     * @description The number of pagination number links before the
     * current page item
     */
    rangeBefore: {
      default: 2,
      type: [Number, String],
    },

    /**
     * @type {Number|String}
     * @description The number of pagination number links after the
     * current page item
     */
    rangeAfter: {
      default: 2,
      type: [Number, String],
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:current', 'change'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const totalModel = computed(() => props.total)

    const currentModel = computed({
      get() {
        return props.current
      },
      set(value) {
        emit('update:current', value)
      },
    })

    /**
     * @returns {Boolean}
     * @description Returns a boolean falg to determine if there is
     * another page of results before the current page.
     */
    const hasPrev = computed(() => {
      return currentModel.value > 1
    })

    /**
     * @returns {Boolean}
     * @description Returns a boolean falg to determine if there is
     * another page of results after the current page.
     */
    const hasNext = computed(() => {
      return currentModel.value < pageCount.value
    })

    /**
     * @returns {Boolean}
     * @description Returns the number of pagination__number links to
     * display before the current page, we are using a computed prop
     * because a string may be passed in, then we would have to call
     * parseInt on it before we can use it in our calculations.
     */
    const beforeCurrent = computed(() => {
      return parseInt(props.rangeBefore)
    })

    /**
     * @returns {Boolean}
     * @description Returns the number of pagination__number links to
     * display after the current page, we are using a computed prop
     * because a string may be passed in, then we would have to call
     * parseInt on it before we can use it in our calculations.
     */
    const afterCurrent = computed(() => {
      return parseInt(props.rangeAfter)
    })

    /**
     * @returns {Number}
     * @description Works out the total number of pages that we have in
     * our dataset, based on the number of items devided by the per page
     * property, these need to be passed through parseInt before calling
     * Math.ceil as we may have been given strings in the total and or
     * perPage props.
     */
    const pageCount = computed(() => {
      return Math.ceil(parseInt(totalModel.value) / parseInt(props.perPage))
    })

    /**
     * @returns {Number}
     * @description First item of the page (count).
     */
    const firstItem = computed(() => {
      const firstItem = currentModel.value * props.perPage - props.perPage + 1
      return firstItem >= 0 ? firstItem : 0
    })

    /**
     * @returns {Array}
     * @description Returns the page number links that are with the ranges
     * set, this is based on the beforeCurrent and afterCurrent properties.
     */
    const pagesInRange = computed(() => {
      let left = Math.max(1, currentModel.value - beforeCurrent.value)
      let right = Math.min(
        currentModel.value + afterCurrent.value,
        pageCount.value
      )
      const pages = []
      for (let i = left; i <= right; i++) {
        pages.push(getPage(i))
      }
      return pages
    })

    /**
     * @param {Event} event
     * @description Previous button click listener.
     */
    const prev = (event) => {
      changePage(currentModel.value - 1, event)
    }

    /**
     * @param {Event} event
     * @description Next button click listener.
     */
    const next = (event) => {
      changePage(currentModel.value + 1, event)
    }

    /**
     * @param {Event} event
     * @description First button click listener.
     */
    const first = (event) => {
      changePage(1, event)
    }

    /**
     * @param {Event} event
     * @description Last button click listener.
     */
    const last = (event) => {
      changePage(pageCount.value, event)
    }

    /**
     * @param {Number} number
     * @param {Event} event
     * @description The click handler for the pagination links this allows
     * each of the links to update the current page number by emiting it.
     * this does however require us to bind our "current" property using
     * the "v-model" directive eg. `v-model:current="current"` The parent can
     * then watch for changes on this to update the data set to accordingly.
     */
    const changePage = (number, event) => {
      if (
        currentModel.value === number ||
        number < 1 ||
        number > pageCount.value
      ) {
        return
      }

      currentModel.value = number
      emit('change', number)

      if (event && event.target) {
        nextTick(() => event.target.focus())
      }
    }

    /**
     * @param {Number} number
     * @param {Object} options
     * @returns {Object}
     * @description Retuns the page config for the requested page
     * number, this holds the click method to change to the requested
     * page, and the classes ect.
     */
    const getPage = (number, options = {}) => {
      return {
        click: (event) => changePage(number, event),
        class: options.class || 'ak-pagination__number',
        disabled: options.disabled || false,
        isCurrent: currentModel.value === number,
        number,
      }
    }

    watch(
      () => pageCount.value,
      (newVal) => {
        if (currentModel.value > newVal) last()
      }
    )

    return {
      currentModel,
      totalModel,
      hasPrev,
      hasNext,
      beforeCurrent,
      afterCurrent,
      pageCount,
      firstItem,
      pagesInRange,
      prev,
      next,
      first,
      last,
      changePage,
      getPage,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
$pagination-item-size: 2.25em;

.ak-pagination {
  vertical-align: middle;
  text-align: center;
  font-weight: bold;
  display: inline-block;
  font-size: $font-size-smaller;

  &__section {
    display: inline-block;
    line-height: $pagination-item-size;
    height: $pagination-item-size;
    vertical-align: middle;
  }

  &__number,
  &__item {
    display: inline-block;
    width: $pagination-item-size;
    height: $pagination-item-size;
    line-height: $pagination-item-size;
    border-radius: 50%;
    border: 0;
    padding: 0;
    font-weight: bold;
    outline: 0;
    background-color: transparent;

    &.is-disabled {
      opacity: 0.8;
    }

    &:focus {
      box-shadow: $form-box-shadow-focus;
    }
  }

  &__item {
    &:not(.is-disabled):hover {
      color: #fff;
      background-color: $color-primary;
    }
    .icon-chevron-left {
      margin-left: -0.25em;
      + .icon-chevron-left {
        margin-left: -0.5em;
      }
    }
    .icon-chevron-right {
      margin-left: 0.125em;
      + .icon-chevron-right {
        margin-left: -0.5em;
      }
    }
  }

  &__label {
    font-size: $font-size-smaller;
  }

  &__numbers {
    list-style: none;
    margin: 0;
    padding: 0 $spacing-small;
    display: flex;

    &-item + &-item {
      margin-left: $spacing-tiny;
    }
  }

  &__number {
    &:hover {
      background: #fff;
      color: $color-primary;
    }
    &.is-active {
      background-color: $color-primary;
      color: #fff;
    }
  }
}
</style>
